import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Modal, ModalContent, ModalBody, Popover, PopoverContent, PopoverTrigger, Skeleton } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import { padZeros, RoleContext } from '@/app/api/common';
import { ChannelDescriptor, copyChannelInfo, copyChannelList, ChannelList } from '@/features/channels/channelsSlice';
import { HotelInfoPublic } from '@/features/projects/projectsSlice';

import { usePageSubscription } from '@/app/websocket/hooks';
import FlexRow from '@/components/Common/FlexRow';
import Grid, { BooleanCell } from '@/components/Common/Grid';
import MenuItem from '@/components/Common/MenuItem';
import { NumberText } from '@/components/Common/Field';

import ChannelListComponent from './ChannelList';

export interface ChannelListsProps {
  project?:				HotelInfoPublic | null;
  registerCallback?:	(cmd: string, callback: () => any) => any;
  setChanges?:			(val: boolean) => any;
  setlocalChannel?:		boolean;
  hideContainer?:		boolean;
  externalChannels?:	any[];
}

const ChannelLists = (props: ChannelListsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const role = React.useContext(RoleContext);
  const logins = useAppSelector((state) => state.logins);

  const canEditLocalList = React.useMemo(() => {
    if (!props.project) return false;
    if (role.admin || role?.privilages?.includes("EditProjectSettings")) return true;

    const aclEnabled = props.project.limits?.acls
      ? props.project.limits.acls.some((u: any) => u.type === "enable-channellist" && u.url === "1")
      : false;
    if (!aclEnabled) return false;
    const username = logins.login?.username;
    if (!username) return false;

    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    const execs = props.project.limits?.executives;
    return !!(execs?.partner?.includes(username) ||
              execs?.chain_manager?.includes(username) ||
              execs?.manager?.includes(username));
  }, [role, logins.login, props.project]);

  usePageSubscription(
    props.project
      ? [{ type: 'local_channel_list', scope_id: props.project.id }]
      : [{ type: 'global_channel_list' }]
  );

  const { epg_id, tab_no } = useParams();
  const globalLists = useAppSelector((state) => state.channelLists);
  const hotelChannelLists = useAppSelector((state) => state.hotelChannelLists);
  
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as ChannelList[]);
  
  const epgId =	tab_no
  				?	tab_no
  				:	epg_id;
  
  const channelLists =	props.project
  						?	hotelChannelLists
  						:	globalLists;
  
  React.useEffect(() => {
    if (props.project) {
      store.getChannelLists(props.project.id, false);
    }
    
    store.getChannelLists();
  }, [props.project]);
  
  React.useEffect(() => {
    setCols([
    {
        field:			"name",
        headerName:		t("channel_lists:grid:name"),
        flex:			1,
        minWidth:		200,
        //maxWidth:		300,
    },
    {
        field:			"channels",
        cellRenderer:	ChannelsCell,
        headerName:		t("channel_lists:grid:channels"),
        flex:			0,
        minWidth:		150,
        maxWidth:		200,
        tooltipValueGetter:		null,
    },
    {
        field:			"hidden",
        headerName:		t("channel_lists:grid:hidden"),
        cellRenderer:	BooleanCell,
        flex:			0,
        filter:			false,
        minWidth:		120,
        maxWidth:		150,
    },
    /*{
        field:			"tags",
        cellRenderer:	TagsCell,
        headerName:		t("channel_lists:grid:tags"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        tooltipValueGetter:		null,
    },*/
    ...((role?.admin || (role?.privilages?.includes("ChangeGlobalChannelLists") || role?.privilages?.includes("ViewGlobalChannelLists") || role?.privilages?.includes("DeleteGlobalChannelList")) || canEditLocalList)
    ?  [{
        field:			"options",
        headerName:		t("channel_lists:grid:options"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] : []),
    ]);
    
    if (channelLists.lists === null)
      return;
    
    const rws: ChannelList[] = [];
    for (const list of channelLists.lists) {
      let row = copyChannelList(list);
      
      if (props.project) {
        let rowA = row as any;
        rowA.project = props.project;
      }
      
      rws.push(row);
    }
    
    setRows(rws);
  }, [channelLists.lists, t]);
  
  const handleCellClick = React.useCallback((evt: any) => {
    if (	evt.colDef.field == "options" ||
        	evt.colDef.field == "channels"
       )
      return;
    if(!role.admin && !props.project && !(role?.privilages?.includes("ChangeGlobalChannelLists") || role?.privilages?.includes("ViewGlobalChannelLists")))
      return;
    navigate(getListPageUrl(evt.data, props.project, "view"));
  }, [navigate, role]);
  
  const updateIsOpen = (val: boolean) => {
    if (!val)
      navigate(getListPageUrl(null, props.project, ""));
      //navigate("/channel_lists");
  };
  
  const handleCommand = (cmd: string) => {
    switch(cmd) {
      
    }
  };
  
  const grid = (
  <Skeleton isLoaded={channelLists.lists !== null}
			className="w-full">
    <Grid rowData={rows}
          columnDefs={cols}
          filter={true}
          showPages={true}
          pageSize={10}
          maxHeight={props.project ? undefined : "calc(100vh - 340px)"}
          onCellClicked={handleCellClick} />
  </Skeleton>
  );
  
  return (
  <React.Fragment>
  {!props.project &&
    <FlexRow direction="column"
	         align="start"
	         className="pt-[32px] px-[32px] bg-white sticky top-[64px] z-10">
      <div className="text-[32px]/[48px]">
        { t('channel_lists:caption') }
      </div>
      <div>
        <NumberText>
          { channelLists.lists === null ? '-' : channelLists.lists.length }
        </NumberText>
        { ' ' + t('channel_lists:lists') }
      </div>
      <Divider className="my-[4px]" />
    </FlexRow> 
  }
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               props.project ? "" : "px-[32px]",
             ]}>
      {(role.admin || (props?.setlocalChannel && (role?.privilages?.includes("EditProjectSettings") || canEditLocalList))) &&
      <FlexRow className="w-full gap-[8px] mb-[16px] justify-between">
        <Button className="bg-[#1B69AA] font-bold rounded-[5px] px-2"
                onClick={() => navigate(getListPageUrl(null, props.project, "__new__"))}>
          <FlexRow justify="center">
            <div className="material-icons !text-white">
              add
            </div>
            <div className="m14 uppercase text-white leading-normal">
              { t('channel_lists:new') }
            </div>
          </FlexRow>
        </Button>
        <Button className="bg-[#1B69AA] font-bold rounded-[5px] px-2"
                onClick={() =>navigate(getListPageUrl(null, props.project, "Import"))}>
          <FlexRow justify="center">
            <div className="material-icons !text-white">
              file_upload 
            </div>
            <div className="m14 uppercase text-white leading-normal">
              { t('channel_lists:options:import') }
            </div>
          </FlexRow>
        </Button>
      </FlexRow>
      }
      { grid }
    </FlexRow>
  {epgId &&
    <Modal backdrop="opaque" 
           defaultOpen={true} 
           isDismissable={false}
           onOpenChange={updateIsOpen}
           classNames={{
             backdrop: "bg-[#0000007f]",
             wrapper: "justify-end"
           }}
           hideCloseButton={true}
           radius="none"
           placement="top">
      <ModalContent className="h-full !mx-0 !my-0 w-full max-w-[620px]">
        <ModalBody className="w-full">
          <FlexRow direction="column"
                   flex={0}>
            <ChannelListComponent {...props} />
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </Modal>
  }
  </React.Fragment>
  );
  
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);
  const logins = useAppSelector((state) => state.logins);
  const canEditLocalList = React.useMemo(() => {
    const proj = props.data?.project;
    if (!proj) return false;
    if (role.admin || role?.privilages?.includes("EditProjectSettings")) return true;

    const aclEnabled = proj.limits?.acls
      ? proj.limits.acls.some((u: any) => u.type === "enable-channellist" && u.url === "1")
      : false;
    if (!aclEnabled) return false;
    const username = logins.login?.username;
    if (!username) return false;

    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === proj.id) return true;

    if (proj.owner === username) return true;
    const execs = proj.limits?.executives;
    return !!(execs?.partner?.includes(username) ||
              execs?.chain_manager?.includes(username) ||
              execs?.manager?.includes(username));
  }, [role, logins.login, props.data]);
  
  const handleMeta = () => {
    setOpen(false);
    navigate(getListPageUrl(props.data, props.data.project, ""))
    //navigate(`/channel_lists/${props.data.epg_id}`)
  };
  
  const handleView = () => {
    setOpen(false);
    navigate(getListPageUrl(props.data, props.data.project, "view"))
    //navigate(`/channel_lists/${props.data.epg_id}/view`);
  };
  
  const handleClone = () => {
    setOpen(false);
    let url = getListPageUrl(null, props.data.project, "__new__");
    const params = new URLSearchParams({
      clone:		props.data.epg_id,
    });
    
    url += "?" + params.toString();
    navigate(url);
  };
  
  const handleDelete = () => {
    setOpen(false);
    navigate(getListPageUrl(props.data, props.data.project, "delete"))
    //navigate(`/channel_lists/${props.data.epg_id}/delete`);
  };
  
  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">

        {
          (role?.admin || (props.data?.project ? canEditLocalList : role?.privilages?.includes("ViewGlobalChannelLists"))) &&
        <MenuItem icon="smart_display"
                  label={t('channel_lists:options:view_list')}
                  onClick={handleView} />
        }
        {
          (role?.admin || (props.data?.project ? canEditLocalList : (role?.privilages?.includes("ViewGlobalChannelLists") && role?.privilages?.includes("ChangeGlobalChannelLists")))) &&
          <MenuItem icon="edit_note"
                    label={t('channel_lists:options:edit_list_meta')}
                    onClick={handleMeta} />
        }
      {  role?.admin &&
          <MenuItem icon="content_copy"
                    label={t('channel_lists:options:clone_list')}
                    onClick={handleClone} />
      }
      {  (role?.admin || (props.data?.project ? canEditLocalList : role?.privilages?.includes("DeleteGlobalChannelList"))) &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('channel_lists:options:delete_list:caption')}
                    onClick={handleDelete} />
      }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

const ChannelsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [open, setOpen] = React.useState(false);
  
  return (
  <FlexRow justify="center">
    <span className="r14">
    {	props.data && props.data.channels
    	?	props.data.channels.length
    	:	""
    }
    </span>
  {	props.data && props.data.channels && props.data.channels.length
  	?
  <Popover key={1}
           placement="bottom-end"
           onOpenChange={setOpen}
           radius="md"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="material-icons cursor-pointer !text-2xl !font-bold !text-[--Dark-Grey]">
        { open ? 'expand_less' : 'navigate_next' }
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[480px] r14">
        <FlexRow align="start"
                 className="max-h-[320px] overflow-auto">
          <FlexRow direction="column"
                   flex={1}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('channel_lists:grid:epg_id') }
            </FlexRow>
          {props.data.channels.map((chan: ChannelDescriptor, i: number) =>
            <FlexRow key={i}
                     className="p-[10px]">
              { chan.epg_id }
            </FlexRow>
          )}
          </FlexRow>
          <FlexRow  direction="column"
                    flex={2}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('channel_lists:grid:name') }
            </FlexRow>
          {props.data.channels.map((chan: ChannelDescriptor, i: number) =>
            <FlexRow key={i}
                     className="p-[10px]">
              { chan.name }
            </FlexRow>
          )}
          </FlexRow>
          <FlexRow  direction="column"
                    flex={1}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('channel_lists:grid:channel_no') }
            </FlexRow>
          {props.data.channels.map((chan: ChannelDescriptor, i: number) =>
            <FlexRow key={i}
                     className="p-[10px]">
              { chan.channel_no }
            </FlexRow>
          )}
          </FlexRow>
        </FlexRow>
      </FlexRow>
    </PopoverContent>
  </Popover>
    :	null
  }
  </FlexRow>
  );
};

export function getListPageUrl(list: ChannelList | null, project?: HotelInfoPublic | null, op?: string) {
  let base =	project
	  			?	`/projects/${encodeURIComponent(project.id)}/channel_lists`
	  			:	'/channel_lists';
  
  if (list)
    base += `/${list.epg_id}`;
  
  if (op)
    base += `/${op}`;
  
  return base;
}

/*const TagsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [open, setOpen] = React.useState(false);
  
  return (
  <FlexRow justify="center">
  {props.data && props.data.urls && props.data.urls.length &&
  <Popover key={1}
           placement="bottom-end"
           onOpenChange={setOpen}
           radius="md"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="text-center">
        <span className="material-icons cursor-pointer !text-2xl !font-bold !text-[--Dark-Grey]">
          { open ? 'expand_less' : 'navigate_next' }
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] r14">
        <FlexRow align="start">
          <FlexRow direction="column"
                   flex={1}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('channels:grid:type') }
            </FlexRow>
          {props.data.urls.map((url: UrlInfo, i: number) =>
            <FlexRow key={i}
                      className="p-[10px]">
              { url.type }
            </FlexRow>
          )}
          </FlexRow>
          <FlexRow  direction="column"
                    flex={3}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('channels:grid:url') }
            </FlexRow>
          {props.data.urls.map((url: UrlInfo, i: number) =>
            <FlexRow key={i}
                     className="p-[10px]">
              { url.url }
            </FlexRow>
          )}
          </FlexRow>
        </FlexRow>
      </FlexRow>
    </PopoverContent>
  </Popover>
  }
  </FlexRow>
  );
};*/

export default ChannelLists;


